import type { ReactNode } from "react";
import { createContext, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  onAuthStateChange,
  onIdTokenChange,
  signIn,
  signOutUser,
  signUp as signUpAuth,
} from "@/apis/auth/auth";
import { RbacProvider } from "@/features/rbac/components/RbacProvider";
import { rbacKeys } from "@/features/rbac/hooks/rbacKeys";
import { CurrentUserProvider } from "@/features/users/components/CurrentUserContext";

import type { User } from "firebase/auth";

/**
 * Authentication context type definition
 */
interface AuthContextType {
  /** Current authenticated user from Firebase Auth or null if not authenticated */
  currentUser: User | null;
  /** Function to sign in with email and password */
  login: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
  /** Function to sign out the current user */
  logout: () => Promise<{ success: boolean; error?: string }>;
  /** Function to create a new user account */
  signup: (
    email: string,
    password: string,
    name: string,
    type: "institution" | "physician" | "patient" | "admin" | "super_admin"
  ) => Promise<{ success: boolean; error?: string }>;
  /** Whether the authentication state is being loaded */
  loading: boolean;
}

/**
 * Props for the AuthProvider component
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Contexto de autenticación
 * @constant {Context<AuthContextType | undefined>} AuthContext - Contexto de autenticación
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Gate de entrada al panel de administración.
 *
 * Se comprueba tanto al iniciar sesión como al RESTAURAR una sesión existente.
 * Antes solo se validaba en `login()`, así que a alguien a quien se le retiraba
 * el acceso le seguía funcionando la pestaña abierta indefinidamente: bastaba
 * con no volver a pasar por la pantalla de inicio de sesión.
 *
 * La lógica vive en `features/staff/services/adminAccess.ts`. Ante un fallo de
 * lectura NO se cierra la sesión: un corte de red pasajero no debe expulsar a
 * nadie. La comprobación dura vive en las reglas de Firestore.
 */
async function checkAccess(
  uid: string,
  email: string | null
): Promise<{ granted: boolean; message: string }> {
  const { checkAdminAccess, accessDeniedMessage } = await import(
    "@/features/staff/services/adminAccess"
  );

  const result = await checkAdminAccess(uid, email);
  return {
    granted: result.granted,
    message: result.granted ? "" : accessDeniedMessage(result.reason),
  };
}

/**
 * Componente de proveedor que maneja el estado de autenticación
 * @param {AuthProviderProps} props - Component props
 * @returns {React.JSX.Element} AuthProvider component
 *
 * @example
 * ```typescript
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  /**
   * Creates a new user account with email and password
   * @param email - User's email address
   * @param password - User's password
   * @returns Promise that resolves with success status
   */
  async function signup(
    email: string,
    password: string,
    name: string,
    type: "institution" | "physician" | "patient" | "admin" | "super_admin"
  ) {
    try {
      const result = await signUpAuth({
        email,
        password,
        confirmPassword: password,
        name,
        type,
      });

      if (result.success) {
        return { success: true };
      } else {
        return { success: false, error: result.message };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Error desconocido",
      };
    }
  }

  /**
   * Signs in a user with email and password
   * @param email - User's email address
   * @param password - User's password
   * @returns Promise that resolves with success status
   */
  async function login(email: string, password: string) {
    try {
      const result = await signIn({ email, password });

      if (result.success) {
        const access = await checkAccess(result.user.uid, result.user.email);

        if (access.granted) {
          return { success: true };
        } else {
          // Si no pertenece al equipo, cerrar sesión de inmediato
          await signOutUser();
          return { success: false, error: access.message };
        }
      } else {
        return { success: false, error: (result as any).message };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Error desconocido",
      };
    }
  }

  /**
   * Signs out the current user
   * @returns Promise that resolves with success status
   */
  async function logout() {
    try {
      const result = await signOutUser();
      // Purgar la caché de permisos: en una máquina compartida, la siguiente
      // persona heredaría los permisos resueltos de la anterior.
      queryClient.removeQueries({ queryKey: rbacKeys.all });
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Error desconocido",
      };
    }
  }

  /**
   * Effect to listen for authentication state changes
   */
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChange(async (user) => {
      if (user && !user.isAnonymous) {
        // Re-verificar el acceso al restaurar la sesión, no solo al iniciarla.
        // Las sesiones anónimas son solo para el link público de instalación:
        // no son personal de ViewMed y no deben cerrarse aquí.
        try {
          const access = await checkAccess(user.uid, user.email);
          if (!access.granted) {
            await signOutUser();
            localStorage.removeItem("authToken");
            setCurrentUser(null);
            setLoading(false);
            return;
          }
        } catch (error) {
          // Un fallo de lectura no expulsa: puede ser un corte de red.
          console.error("No se pudo verificar el acceso al panel:", error);
        }
      }

      setCurrentUser(user);
      if (user) {
        // Obtener token inicial cuando el usuario se autentica
        try {
          const token = await user.getIdToken();
          localStorage.setItem("authToken", token);
        } catch (error) {
          console.error("Error obteniendo token inicial:", error);
          localStorage.removeItem("authToken");
        }
      } else {
        // Limpiar token cuando el usuario se desautentica
        localStorage.removeItem("authToken");
      }
      setLoading(false);
    });

    return unsubscribeAuth;
  }, []);

  /**
   * Effect to listen for ID token changes (automatic refresh)
   */
  useEffect(() => {
    const unsubscribeToken = onIdTokenChange(async (user) => {
      if (user) {
        try {
          // Obtener token fresco cuando cambia
          const token = await user.getIdToken();
          localStorage.setItem("authToken", token);
        } catch (error) {
          console.error("Error refrescando token:", error);
          localStorage.removeItem("authToken");
        }
      }
    });

    return unsubscribeToken;
  }, []);

  /**
   * Valor del contexto de autenticación
   * @constant {AuthContextType} value - Valor del contexto de autenticación
   */
  const value: AuthContextType = {
    currentUser,
    login,
    logout,
    signup,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && (
        <CurrentUserProvider authUid={currentUser?.uid || null}>
          <RbacProvider>{children}</RbacProvider>
        </CurrentUserProvider>
      )}
    </AuthContext.Provider>
  );
}

export { AuthContext };
